import i18n from 'i18next';
import { GetState, SetState } from 'zustand';

import { environment } from '../../environments/environment';
import { LoginPayload } from '../../models/payloads/login.payload';
import { UpdateUserPayload } from '../../models/payloads/update-user.payload';
import { SentEmailProxy } from '../../models/proxies/sent-email.proxy';
import { TokenProxy } from '../../models/proxies/token.proxy';
import { UserProxy } from '../../models/proxies/user.proxy';
import api from '../../services/api';
import { getErrorMessage } from '../../utils/query';
import { UseAuthStore } from './models';

export function getTokenProxyFromAPI(set: SetState<UseAuthStore>): UseAuthStore['getTokenProxyFromAPI'] {
  return async function (authPath: string, payload: LoginPayload) {
    try {
      const url = environment.api.auth.local.replace('{authType}', authPath);
      const { data: jwt } = await api.post<TokenProxy>(url, payload);

      set({ jwt });
    } catch (error) {
      if (error.__CANCEL__) return;

      throw new Error(getErrorMessage(error.response?.data, i18n.t('error.cannotLogin')));
    }
  };
}

export function getInfoAboutCurrentUserFromAPI(
  set: SetState<UseAuthStore>
): UseAuthStore['getInfoAboutCurrentUserFromAPI'] {
  return async function () {
    try {
      const { data: user } = await api.get<UserProxy>(environment.api.user.me);

      set({ user, isLogged: true });
    } catch (error) {
      resetState(set)();

      if (error.__CANCEL__) return;

      throw new Error(getErrorMessage(error.response?.data, i18n.t('error.cannotGetUserInfo')));
    }
  };
}

export function createUserFromAPI(set: SetState<UseAuthStore>): UseAuthStore['createUserFromAPI'] {
  return async function (payload) {
    try {
      const { data } = await api.post<UserProxy>(environment.api.user.create, payload);

      return data;
    } catch (error) {
      resetState(set)();

      if (error.__CANCEL__) return;

      throw new Error(getErrorMessage(error.response?.data, i18n.t('error.cannotCreateUser')));
    }
  };
}

export function updateUserFromAPI(
  set: SetState<UseAuthStore>,
  get: GetState<UseAuthStore>
): UseAuthStore['updateUserFromAPI'] {
  return async function (payload: UpdateUserPayload) {
    try {
      const user = get().user;
      const url = environment.api.user.update.replace('{userId}', String(user.id));

      const { data: userUpdated } = await api.put<UserProxy | undefined>(url, payload);

      await get().getInfoAboutCurrentUserFromAPI();

      return userUpdated;
    } catch (error) {
      if (error.__CANCEL__) return;

      throw new Error(getErrorMessage(error.response?.data, i18n.t('error.cannotUpdateUser')));
    }
  };
}

export function sendForgotEmailFromAPI(
  set: SetState<UseAuthStore>,
  get: GetState<UseAuthStore>
): UseAuthStore['sendForgotEmailFromAPI'] {
  return async function (email: string) {
    try {
      const url = environment.api.user.forgotEmail.replace('{email}', email);

      const { data } = await api.post<SentEmailProxy | undefined>(url);

      return data;
    } catch (error) {
      if (error.__CANCEL__) return;

      throw new Error(getErrorMessage(error.response?.data, i18n.t('error.cannotSentEmail')));
    }
  };
}

export function sendResetPasswordFromAPI(
  set: SetState<UseAuthStore>,
  get: GetState<UseAuthStore>
): UseAuthStore['sendResetPasswordFromAPI'] {
  return async function (code: string, newPassword: string) {
    try {
      const url = environment.api.user.resetPassword.replace('{code}', code);

      await api.post<void>(url, {
        newPassword,
      });
    } catch (error) {
      if (error.__CANCEL__) return;

      throw new Error(getErrorMessage(error.response?.data, i18n.t('error.cannotResetPassword')));
    }
  };
}

export function resetState(set: SetState<UseAuthStore>): () => void {
  return function () {
    set({
      isLogged: false,
      jwt: null,
      user: null,
    });
  };
}
